import React, { useState, useEffect } from 'react';
import { X, Calendar as CalendarIcon, Clock, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Calendar } from './ui/calendar';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { convertTime, getTimezoneAbbr } from '../utils/timezoneUtils';
import LegalDisclaimerBox from './LegalDisclaimerBox';
import { BookingContactInfo } from './BookingContactInfo';

const BookingModal = ({ trainer, onClose }) => {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [duration, setDuration] = useState(60);
  const [customerMessage, setCustomerMessage] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [courseType, setCourseType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookedTimes, setBookedTimes] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [noAvailability, setNoAvailability] = useState(false);
  const [noAvailabilityMessage, setNoAvailabilityMessage] = useState('');
  const [availableDays, setAvailableDays] = useState(null);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  // Fetch trainer's weekly availability to know which days to enable in calendar
  useEffect(() => {
    const fetchTrainerAvailability = async () => {
      if (!trainer?.id) return;
      
      try {
        const response = await fetch(`${API_BASE}/api/trainer/${trainer.id}/availability`);
        if (response.ok) {
          const data = await response.json();
          const weeklySchedule = data.weekly_schedule || {};
          
          // Check if trainer has any availability set
          const hasAnyAvailability = Object.values(weeklySchedule).some(
            day => day.enabled && day.slots?.length > 0
          );
          
          if (hasAnyAvailability) {
            // Map day names to JavaScript day numbers (0 = Sunday, 1 = Monday, etc.)
            const dayMap = {
              sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
              thursday: 4, friday: 5, saturday: 6
            };
            
            const enabledDays = Object.entries(weeklySchedule)
              .filter(([_, dayData]) => dayData.enabled && dayData.slots?.length > 0)
              .map(([dayName, _]) => dayMap[dayName]);
            
            setAvailableDays(enabledDays);
          } else {
            // No availability set, allow all days
            setAvailableDays(null);
          }
        }
      } catch (error) {
        console.error('Error fetching trainer availability:', error);
        setAvailableDays(null); // On error, allow all days
      }
    };
    
    fetchTrainerAvailability();
  }, [trainer?.id, API_BASE]);

  // Pre-fill user email and phone from user profile
  useEffect(() => {
    if (user) {
      setCustomerEmail(user.email || '');
      setCustomerPhone(user.phone || '');
    }
  }, [user]);

  // Default time slots (fallback if no availability set)
  const defaultTimeSlots = [
    '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', 
    '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'
  ];

  // Fetch available and booked time slots when date is selected
  useEffect(() => {
    const fetchSlots = async () => {
      if (!selectedDate || !trainer?.id) return;
      
      setLoadingSlots(true);
      setSelectedTime(''); // Reset selected time when date changes
      setNoAvailability(false);
      setNoAvailabilityMessage('');
      
      try {
        // Format date as YYYY-MM-DD using local date (not UTC)
        // This prevents timezone issues where toISOString() shifts the date
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        
        // Fetch available slots from trainer's availability
        const availableResponse = await fetch(`${API_BASE}/api/trainer/${trainer.id}/available-slots?date=${dateStr}`);
        
        if (availableResponse.ok) {
          const availableData = await availableResponse.json();
          
          // If trainer has set availability and has slots for this day
          if (availableData.available_slots && availableData.available_slots.length > 0) {
            setAvailableSlots(availableData.available_slots);
            setNoAvailability(false);
          } else if (availableData.message) {
            // Trainer has availability settings but is NOT available on this day
            setAvailableSlots([]);
            setNoAvailability(true);
            setNoAvailabilityMessage(availableData.message);
          } else {
            // No availability set at all, use defaults
            setAvailableSlots(defaultTimeSlots);
            setNoAvailability(false);
          }
          setBookedTimes(availableData.booked_slots || []);
        } else {
          // Fallback to old endpoint
          const response = await fetch(`${API_BASE}/api/bookings/booked-slots/${trainer.id}/${dateStr}`);
          if (response.ok) {
            const data = await response.json();
            setBookedTimes(data.booked_times || []);
            setAvailableSlots(defaultTimeSlots);
          }
        }
      } catch (error) {
        console.error('Error fetching slots:', error);
        setBookedTimes([]);
        setAvailableSlots(defaultTimeSlots);
      } finally {
        setLoadingSlots(false);
      }
    };

    fetchSlots();
  }, [selectedDate, trainer?.id, API_BASE]);

  // Use available slots (from trainer's availability or defaults)
  const timeSlots = availableSlots.length > 0 ? availableSlots : defaultTimeSlots;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime) {
      toast({
        title: 'Incomplete Booking',
        description: 'Please select both date and time',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast({
          title: 'Authentication Required',
          description: 'Please login to book a session',
          variant: 'destructive',
        });
        return;
      }

      // Create booking - use local date format to avoid timezone issues
      const bookingYear = selectedDate.getFullYear();
      const bookingMonth = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const bookingDay = String(selectedDate.getDate()).padStart(2, '0');
      const bookingDateStr = `${bookingYear}-${bookingMonth}-${bookingDay}`;
      
      // Combine all trainer specializations for the booking
      const trainerSpecializations = trainer.specializations && trainer.specializations.length > 0
        ? trainer.specializations.join(', ')
        : 'Training';
      
      const bookingData = {
        trainer_id: trainer.id,
        date: bookingDateStr,
        time: selectedTime,
        duration: duration,
        specialization: trainerSpecializations,
        price: totalPrice,
        customer_message: customerMessage || null,
        customer_phone: customerPhone || null,
        customer_email: customerEmail || null,
        course_type: courseType || null
      };

      const response = await fetch(`${API_BASE}/api/bookings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bookingData)
      });

      if (response.ok) {
        toast({
          title: 'Booking Requested',
          description: `Your session with ${trainer.name} on ${selectedDate.toLocaleDateString()} at ${selectedTime} has been requested. Waiting for trainer confirmation.`,
        });
        onClose();
        
        // Refresh page to update dashboard
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        const error = await response.json();
        toast({
          title: 'Booking Failed',
          description: error.detail || 'Failed to create booking',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      toast({
        title: 'Error',
        description: 'An error occurred while creating booking',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalPrice = (trainer.pricePerHour * duration) / 60;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Book a Session</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Personalized Greeting */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-700">
            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Hello {user?.name || 'there'},</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
              My name is <span className="font-semibold text-gray-900 dark:text-white">{trainer.name}</span>, I am looking forward to training with you.
            </p>
          </div>

          {/* Customer Message */}
          <div>
            <Label htmlFor="customerMessage" className="text-base font-semibold mb-2 block text-gray-900 dark:text-white">
              Introduce Yourself & Add Notes
            </Label>
            <textarea
              id="customerMessage"
              value={customerMessage}
              onChange={(e) => setCustomerMessage(e.target.value)}
              placeholder="Tell the trainer about yourself, your goals, and any specific requirements..."
              className="w-full min-h-[120px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              rows="5"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Example: "My name is John, I am looking for a Martial Arts trainer. I would like to start lessons as soon as possible."
            </p>
          </div>

          {/* Contact Information Section */}
          <BookingContactInfo
            customerPhone={customerPhone}
            setCustomerPhone={setCustomerPhone}
            customerEmail={customerEmail}
            setCustomerEmail={setCustomerEmail}
          />

          {/* Course Type Selection */}
          {trainer.training_type === 'both' && (
            <div>
              <Label htmlFor="courseType" className="text-base font-semibold mb-2 block text-gray-900 dark:text-white">
                Course Type
              </Label>
              <select
                id="courseType"
                value={courseType}
                onChange={(e) => setCourseType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="" className="bg-white dark:bg-gray-700">Select date first</option>
                <option value="online" className="bg-white dark:bg-gray-700">Online</option>
                <option value="in_person" className="bg-white dark:bg-gray-700">In Person</option>
              </select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                * If the trainer provides both types of services, you can choose.
              </p>
            </div>
          )}

          {/* Date Selection */}
          <div>
            <Label className="text-base font-semibold mb-3 block text-gray-900 dark:text-white">Course Date</Label>
            <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex justify-center bg-white dark:bg-gray-700/50">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={(date) => {
                  // Disable past dates
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  if (date < today) return true;
                  
                  // If trainer has specific available days, disable other days
                  if (availableDays !== null && availableDays.length > 0) {
                    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
                    return !availableDays.includes(dayOfWeek);
                  }
                  
                  return false;
                }}
                className="rounded-md"
              />
            </div>
            {availableDays !== null && availableDays.length > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                📅 Trainer is available on: {availableDays.map(d => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]).join(', ')}
              </p>
            )}
          </div>

          {/* Time Selection */}
          <div>
            <Label className="text-base font-semibold mb-3 block text-gray-900 dark:text-white">Course Time</Label>
            
            {/* Timezone Info Banner */}
            {trainer?.timezone && user?.timezone && trainer.timezone !== user.timezone && (
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                <div className="flex items-start gap-2">
                  <Globe className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="text-blue-800 dark:text-blue-300 font-medium">Different Time Zones</p>
                    <p className="text-blue-600 dark:text-blue-400 text-xs mt-1">
                      Times shown are in trainer's timezone ({getTimezoneAbbr(trainer.timezone)}). 
                      Your local time ({getTimezoneAbbr(user.timezone)}) is shown below each slot.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {!selectedDate ? (
              <div className="text-center py-6 px-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
                <Clock className="w-8 h-8 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Please select a date first to see available time slots
                </p>
              </div>
            ) : loadingSlots ? (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">Loading available times...</div>
            ) : noAvailability ? (
              <div className="text-center py-8 px-4 bg-amber-50 dark:bg-amber-900/10 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 mb-3">
                  <CalendarIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
                <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                  Not Available
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {noAvailabilityMessage || 'The trainer is not available on this day.'}
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {timeSlots.map((time) => {
                    const isBooked = bookedTimes.includes(time);
                    const trainerTimezone = trainer?.timezone;
                    const userTimezone = user?.timezone;
                    const hasDifferentTimezone = trainerTimezone && userTimezone && trainerTimezone !== userTimezone;
                    
                    // Convert trainer's time to user's local time (using local date format)
                    const localYear = selectedDate.getFullYear();
                    const localMonth = String(selectedDate.getMonth() + 1).padStart(2, '0');
                    const localDay = String(selectedDate.getDate()).padStart(2, '0');
                    const localDateStr = `${localYear}-${localMonth}-${localDay}`;
                    
                    const userLocalTime = hasDifferentTimezone && selectedDate
                      ? convertTime(time, localDateStr, trainerTimezone, userTimezone)
                      : time;
                    
                    return (
                      <button
                        key={time}
                        type="button"
                        onClick={() => !isBooked && setSelectedTime(time)}
                        disabled={isBooked}
                        className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                          isBooked
                            ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-600 cursor-not-allowed'
                            : selectedTime === time
                              ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white border-blue-600 shadow-lg'
                              : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 hover:shadow-md'
                        }`}
                      >
                        {isBooked ? (
                          <div className="text-center">
                            <span className="line-through">{time}</span>
                            <span className="block text-xs mt-1">Booked</span>
                          </div>
                        ) : hasDifferentTimezone ? (
                          <div className="space-y-1.5">
                            {/* Trainer's Time */}
                            <div className={`flex items-center justify-center gap-1.5 px-2 py-1 rounded ${
                              selectedTime === time ? 'bg-blue-500/30' : 'bg-blue-100 dark:bg-blue-900/40'
                            }`}>
                              <Clock className={`w-3 h-3 ${selectedTime === time ? 'text-white' : 'text-blue-600 dark:text-blue-400'}`} />
                              <span className={`text-xs font-medium ${selectedTime === time ? 'text-white' : 'text-blue-700 dark:text-blue-300'}`}>
                                Trainer: {time} {getTimezoneAbbr(trainerTimezone)}
                              </span>
                            </div>
                            {/* User's Time */}
                            <div className={`flex items-center justify-center gap-1.5 px-2 py-1 rounded ${
                              selectedTime === time ? 'bg-green-500/30' : 'bg-green-100 dark:bg-green-900/40'
                            }`}>
                              <Globe className={`w-3 h-3 ${selectedTime === time ? 'text-white' : 'text-green-600 dark:text-green-400'}`} />
                              <span className={`text-xs font-medium ${selectedTime === time ? 'text-white' : 'text-green-700 dark:text-green-300'}`}>
                                Your time: {userLocalTime} {getTimezoneAbbr(userTimezone)}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-1">
                            <span className="text-base">{time}</span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                {bookedTimes.length > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                    ⚠️ Grayed out times are already booked for this date
                  </p>
                )}
              </>
            )}
          </div>

          {/* Duration */}
          <div>
            <Label className="text-base font-semibold mb-3 block text-gray-900 dark:text-white">Duration</Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setDuration(30)}
                className={`p-4 rounded-lg border-2 transition-all text-center ${
                  duration === 30
                    ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/30 ring-2 ring-blue-500/30'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                }`}
              >
                <div className="text-2xl font-bold text-gray-900 dark:text-white">30</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">minutes</div>
                <div className="text-lg font-semibold text-blue-600 dark:text-blue-400 mt-1">
                  ${((trainer.pricePerHour * 30) / 60).toFixed(2)}
                </div>
              </button>
              <button
                type="button"
                onClick={() => setDuration(60)}
                className={`p-4 rounded-lg border-2 transition-all text-center ${
                  duration === 60
                    ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/30 ring-2 ring-blue-500/30'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                }`}
              >
                <div className="text-2xl font-bold text-gray-900 dark:text-white">60</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">minutes</div>
                <div className="text-lg font-semibold text-blue-600 dark:text-blue-400 mt-1">
                  ${trainer.pricePerHour.toFixed(2)}
                </div>
              </button>
            </div>
          </div>

          {/* Total Price */}
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-700">
            <div className="flex items-center justify-between">
              <span className="text-gray-700 dark:text-gray-300 font-medium">Total Price:</span>
              <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">${totalPrice.toFixed(2)}</span>
            </div>
          </div>

          {/* Payment Legal Disclaimer - Dynamic from API */}
          <LegalDisclaimerBox variant="full" className="border-2 border-amber-300 dark:border-amber-600" />

          {/* Important Information Notes */}
          <div className="space-y-2">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3 text-sm text-blue-800 dark:text-blue-300">
              <p><strong>ℹ️ Information:</strong> Your contact information (phone number and email address) will be shared with the trainer.</p>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-3 text-sm text-green-800 dark:text-green-300">
              <p><strong>✉️ Email Notification:</strong> If the trainer accepts your request you will receive an email.</p>
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3 text-sm text-yellow-800 dark:text-yellow-300">
              <p><strong>⏳ Note:</strong> This is a booking request. The trainer will confirm your session within 24 hours. Payment is processed after trainer confirmation.</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700" disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={isSubmitting}>
              {isSubmitting ? 'Requesting...' : 'Confirm'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookingModal;